import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";

export type SubscriptionPlan = "starter" | "pro" | "enterprise" | "solo";
export type SubscriptionStatus = "trialing" | "active" | "past_due" | "canceled" | "paused" | "unpaid" | "incomplete";
export type OrgMemberRole = "owner" | "admin" | "manager" | "member";
export type OrgMemberType = "internal" | "provider";

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  is_provider: boolean;
  created_at: string;
  created_by: string | null;
  settings: Record<string, unknown>;
  // Fiscal data fields
  legal_name: string | null;
  trading_name: string | null;
  tax_id: string | null;
  state_registration: string | null;
  municipal_registration: string | null;
  fiscal_email: string | null;
  phone: string | null;
  address_street: string | null;
  address_number: string | null;
  address_complement: string | null;
  address_neighborhood: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrgMemberRole;
  member_type: OrgMemberType;
  invited_by: string | null;
  joined_at: string;
  profile?: {
    display_name: string | null;
    email?: string;
  };
}

export interface OrganizationSubscription {
  id: string;
  organization_id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  user_limit: number;
  extra_storage_gb: number | null;
  trial_ends_at: string | null;
  grace_period_days: number;
  past_due_since: string | null;
  canceled_at: string | null;
  cancellation_type: string | null;
  cancellation_access_until: string | null;
  export_only_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionUsage {
  id: string;
  organization_id: string;
  users_count: number;
  contacts_count: number;
  opportunities_count: number;
  automations_count: number;
  storage_used_bytes: number;
  updated_at: string;
}

interface OrganizationContextType {
  // Current organization
  organization: Organization | null;
  subscription: OrganizationSubscription | null;
  usage: SubscriptionUsage | null;
  members: OrganizationMember[];
  
  // User's membership info
  currentMembership: OrganizationMember | null;
  isOrgAdmin: boolean;
  isOrgOwner: boolean;
  isProviderMember: boolean;
  
  // All accessible organizations (for org switcher)
  accessibleOrganizations: Organization[];
  
  // Loading states
  isLoading: boolean;
  
  // Actions
  switchOrganization: (orgId: string) => Promise<void>;
  refreshOrganization: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, userRoles } = useAuth();
  
  // Check if user is a platform admin (global admin, editor or suporte)
  const isPlatformAdmin = useMemo(() => 
    userRoles.includes('admin') || userRoles.includes('editor') || userRoles.includes('suporte'),
    [userRoles]
  );
  
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [subscription, setSubscription] = useState<OrganizationSubscription | null>(null);
  const [usage, setUsage] = useState<SubscriptionUsage | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [currentMembership, setCurrentMembership] = useState<OrganizationMember | null>(null);
  const [accessibleOrganizations, setAccessibleOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProviderMember, setIsProviderMember] = useState(false);

  const isOrgAdmin = currentMembership?.role === 'admin' || currentMembership?.role === 'owner';
  const isOrgOwner = currentMembership?.role === 'owner';

  // Get stored org ID from localStorage
  const getStoredOrgId = (): string | null => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('currentOrganizationId');
  };

  // Store org ID in localStorage
  const storeOrgId = (orgId: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('currentOrganizationId', orgId);
    }
  };

  // Load organization data
  const loadOrganizationData = async (orgId: string) => {
    if (!user) return;

    try {
      // Fetch organization details
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      if (orgError) throw orgError;
      
      // Cast to proper type
      const org: Organization = {
        ...orgData,
        settings: (orgData.settings as Record<string, unknown>) || {}
      };
      setOrganization(org);

      // Fetch subscription
      const { data: subData } = await supabase
        .from('organization_subscriptions')
        .select('*')
        .eq('organization_id', orgId)
        .single();

      if (subData) {
        setSubscription(subData as OrganizationSubscription);
      }

      // Fetch usage
      const { data: usageData } = await supabase
        .from('subscription_usage')
        .select('*')
        .eq('organization_id', orgId)
        .single();

      if (usageData) {
        setUsage(usageData as SubscriptionUsage);
      }

      // Fetch members with profiles
      const { data: membersData } = await supabase
        .from('organization_members')
        .select('*')
        .eq('organization_id', orgId);

      if (membersData) {
        // Fetch profiles for members
        const userIds = membersData.map(m => m.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', userIds);

        const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
        
        const membersWithProfiles: OrganizationMember[] = membersData.map(m => ({
          ...m,
          role: m.role as OrgMemberRole,
          member_type: m.member_type as OrgMemberType,
          profile: profileMap.get(m.user_id) || undefined
        }));

        setMembers(membersWithProfiles);

        // Find current user's membership
        const myMembership = membersWithProfiles.find(m => m.user_id === user.id);
        
        // Create virtual membership for platform admins without real membership
        if (!myMembership && isPlatformAdmin) {
          const virtualMembership: OrganizationMember = {
            id: 'virtual-support-member',
            organization_id: orgId,
            user_id: user.id,
            role: 'owner',
            member_type: 'provider',
            invited_by: null,
            joined_at: new Date().toISOString(),
            profile: { display_name: 'Suporte NeoSDR' }
          };
          setCurrentMembership(virtualMembership);
        } else {
          setCurrentMembership(myMembership || null);
        }
      }

      storeOrgId(orgId);
    } catch (error) {
      console.error('Error loading organization data:', error);
    }
  };

  // Check if user is provider member
  const checkProviderMember = async () => {
    if (!user) return false;

    const { data } = await supabase.rpc('is_provider_member', { _user_id: user.id });
    return data ?? false;
  };

  // Load accessible organizations
  const loadAccessibleOrganizations = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('name');

      if (error) throw error;
      
      const orgs: Organization[] = (data || []).map(o => ({
        ...o,
        settings: (o.settings as Record<string, unknown>) || {}
      }));
      setAccessibleOrganizations(orgs);
      return orgs;
    } catch (error) {
      console.error('Error loading accessible organizations:', error);
      return [];
    }
  };

  // Initialize organization context
  useEffect(() => {
    if (!user) {
      setOrganization(null);
      setSubscription(null);
      setUsage(null);
      setMembers([]);
      setCurrentMembership(null);
      setAccessibleOrganizations([]);
      setIsLoading(false);
      return;
    }

    const initialize = async () => {
      setIsLoading(true);
      try {
        // Check provider status
        const isProvider = await checkProviderMember();
        setIsProviderMember(isProvider);

        // Load accessible organizations
        const orgs = await loadAccessibleOrganizations();

        if (!orgs || orgs.length === 0) {
          setIsLoading(false);
          return;
        }

        // Determine which org to load
        const storedOrgId = getStoredOrgId();
        let targetOrgId: string;

        if (storedOrgId && orgs.some(o => o.id === storedOrgId)) {
          targetOrgId = storedOrgId;
        } else {
          // Default to first org (usually user's primary org)
          targetOrgId = orgs[0].id;
        }

        await loadOrganizationData(targetOrgId);
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [user]);

  // Switch to a different organization
  const switchOrganization = async (orgId: string) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      await loadOrganizationData(orgId);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh current organization data
  const refreshOrganization = async () => {
    if (!organization) return;
    await loadOrganizationData(organization.id);
  };

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        subscription,
        usage,
        members,
        currentMembership,
        isOrgAdmin,
        isOrgOwner,
        isProviderMember,
        accessibleOrganizations,
        isLoading,
        switchOrganization,
        refreshOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
